import InfoIcon from "@mui/icons-material/Info";
import { LoadingButton } from "@mui/lab";
import { Box, Button, ButtonOwnProps, DialogProps, TextField, Typography } from "@mui/material";
import { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";

import { AGDialog } from "../AGDialog";
import "./index.css";

interface IConfirmationDialogProps {
	open: boolean;
	title: string;
	heading: string;
	warning?: string;
	warningColor?: string;
	/** @default "button" */
	confirmationType?: "input" | "button";
	color?: ButtonOwnProps["color"];
	confirmButtonLoading?: boolean;
	width?: DialogProps["maxWidth"];
	/** @default "Proceed" */
	submitButtonText?: string;
	/** @default "confirm" */
	confirmationText?: string;
	/** @default "Cancel" */
	cancelButtonText?: string;
	onClose: () => void;
	onConfirm: () => void;
}

const ConfirmationDialog: FC<IConfirmationDialogProps> = (props) => {
	const {
		open,
		title,
		heading,
		warning,
		warningColor,
		confirmationType = "button",
		color,
		confirmButtonLoading,
		width,
		submitButtonText = "Proceed",
		confirmationText = "confirm",
		cancelButtonText = "Cancel",
		onClose,
		onConfirm
	} = props;

	const [confirmationInput, setConfirmationInput] = useState<string>("");

	function handleChangeConfirmationInput(event: ChangeEvent<HTMLInputElement>) {
		setConfirmationInput(event.target.value);
	}

	function handleConfirmationFormSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		onConfirm();
	}

	useEffect(() => {
		if (open) setConfirmationInput("");
	}, [open]);

	return (
		<AGDialog open={open} onClose={onClose} maxWidth={width} title={title} color={color}>
			<Box component="form" noValidate onSubmit={handleConfirmationFormSubmit}>
				<Typography variant="h6" textAlign="center">
					{heading}
				</Typography>

				{warning ? (
					<Box className="confirmation-info-section" sx={warningColor ? { color: warningColor } : {}}>
						<InfoIcon color="inherit" fontSize="inherit" />

						<Typography textAlign="center" color="inherit">
							{warning}
						</Typography>
					</Box>
				) : null}

				{confirmationType === "input" ? (
					<Typography textAlign="center" color="#474747">
						{`Please type '${confirmationText}' below before proceeding`}
					</Typography>
				) : null}

				{confirmationType === "input" ? (
					<Box className="confirmation-input-section-wrapper">
						<Box className="confirmation-input-wrapper">
							<TextField
								fullWidth
								autoFocus={!navigator.userAgent.match(/Android|webOS|iPhone|iPod|Blackberry/i)}
								variant="standard"
								color="secondary"
								placeholder={`Please type ${confirmationText} here`}
								slotProps={{ htmlInput: { style: { textAlign: "center" } } }}
								value={confirmationInput}
								onChange={handleChangeConfirmationInput}
								autoComplete="off"
							/>
						</Box>
					</Box>
				) : null}

				<Box className="confirmation-dialog-actions-wrapper">
					<Button variant="outlined" color="error" onClick={onClose}>
						{cancelButtonText}
					</Button>

					<LoadingButton
						disableElevation
						variant="contained"
						color={color ? color : "primary"}
						type="submit"
						disabled={confirmationType === "input" && confirmationInput.toLowerCase() !== confirmationText}
						loading={confirmButtonLoading}
					>
						{submitButtonText}
					</LoadingButton>
				</Box>
			</Box>
		</AGDialog>
	);
};

export default ConfirmationDialog;
